import { REFERENCE_CACHE } from './cache-key.js';
import { SYNC_TAG } from './db.js';
import { drainQueue } from './sync.js';
import type { DrainResult } from './sync.js';

/**
 * Registering the service worker, and everything that has to happen from the
 * page rather than inside it.
 *
 * ## Why the page drains the queue too
 *
 * Background Sync exists in Chrome and Edge and in neither Safari nor Firefox.
 * On an iPhone — which is the device this application's offline mode was built
 * for, because that is what is in somebody's pocket in a car park — the only
 * moment the queue can be drained is while the app is open.
 *
 * So there are three triggers, and they are allowed to overlap:
 *
 *   - the app starting, which covers "opened it again later";
 *   - the `online` event, which covers "signal came back while it was open";
 *   - the worker's `sync`, where the browser has it.
 *
 * Every queued item carries the idempotency key it was created with, so two
 * drains racing costs one wasted request and never a duplicate receipt.
 */

export type DrainListener = (result: DrainResult) => void;

const listeners = new Set<DrainListener>();

/** Be told when the queue moves. Returns the unsubscribe. */
export function onDrain(listener: DrainListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function announce(result: DrainResult): void {
  for (const listener of listeners) {
    listener(result);
  }
}

/**
 * Drain, and tell the page what happened.
 *
 * Serialised: two overlapping drains would both mint a token and both walk the
 * same items, and while that is safe it is pure waste on a metered connection.
 */
let inFlight: Promise<DrainResult> | null = null;

export async function drainNow(): Promise<DrainResult> {
  if (inFlight !== null) {
    return inFlight;
  }
  inFlight = drainQueue()
    .then((result) => {
      announce(result);
      return result;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/**
 * Ask the browser to drain the queue later, if it can.
 *
 * `SyncManager` is not in every browser's type library and is absent at runtime
 * in Safari and Firefox, so this is a feature test rather than a cast: a missing
 * Background Sync must degrade to "the page will do it", not throw on a phone
 * standing in a car park.
 */
export async function requestBackgroundSync(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false;
  }
  const registration = await navigator.serviceWorker.ready;
  const sync = (registration as ServiceWorkerRegistration & {
    sync?: { register: (tag: string) => Promise<void> };
  }).sync;
  if (sync === undefined) {
    return false;
  }
  try {
    await sync.register(SYNC_TAG);
    return true;
  } catch {
    // Chrome refuses the registration when the user has blocked background
    // sync for the site. Falling back to the page's own drain is correct.
    return false;
  }
}

export interface UpdatePrompt {
  /** A new version is waiting; reloading takes it. */
  readonly onUpdateAvailable: () => void;
}

/**
 * Register the worker.
 *
 * Called once, from the application root. It is a no-op where service workers
 * are unavailable — an old browser, or any page not served over https — and
 * that is a supported state rather than a failure: everything except installing
 * and offline capture works exactly as before.
 */
export function registerServiceWorker(prompt: UpdatePrompt): void {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const url = import.meta.env.DEV ? '/dev-sw.js?dev-sw' : '/sw.js';
  const type = import.meta.env.DEV ? 'module' : 'classic';

  void navigator.serviceWorker
    .register(url, { type, scope: '/' })
    .then((registration) => {
      if (registration.waiting !== null) {
        prompt.onUpdateAvailable();
      }
      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (installing === null) {
          return;
        }
        installing.addEventListener('statechange', () => {
          /*
            `controller` is null on the very first install, and that is not an
            update — telling a first-time visitor that a new version is ready
            is nonsense. It is only an update when something was already in
            charge of this page.
          */
          if (installing.state === 'installed' && navigator.serviceWorker.controller !== null) {
            prompt.onUpdateAvailable();
          }
        });
      });
    })
    .catch(() => {
      // Registration failing is survivable and common in development behind a
      // proxy. The app works; it is simply not installable this session.
    });

  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as { type?: string; sent?: number } | null;
    if (data?.type === 'QUEUE_DRAINED') {
      announce({ sent: data.sent ?? 0, failed: 0, remaining: 0 });
    }
  });
}

/** Take the waiting version. The page reloads when it takes over. */
export async function applyUpdate(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }
  const registration = await navigator.serviceWorker.getRegistration();
  registration?.waiting?.postMessage({ type: 'SKIP_WAITING' });
  // `controllerchange` fires once the new worker takes over; reloading then
  // rather than immediately means the reload is served by the new version.
  navigator.serviceWorker.addEventListener(
    'controllerchange',
    () => {
      window.location.reload();
    },
    { once: true },
  );
}

/**
 * Start the triggers that are the page's responsibility.
 *
 * Returns the teardown, so a test can mount and unmount without leaving a
 * listener on `window` that fires against a torn-down React tree.
 */
export function startQueueDraining(): () => void {
  const onOnline = (): void => {
    void drainNow();
  };
  window.addEventListener('online', onOnline);

  if (navigator.onLine) {
    void drainNow();
  }
  void requestBackgroundSync();

  return () => {
    window.removeEventListener('online', onOnline);
  };
}

/**
 * Forget the cached reference data.
 *
 * Called when the person signs out. The entries are already keyed by company
 * and user, so nobody else could be served them; this is about not leaving a
 * former session's chart of accounts and company details in the browser at all.
 *
 * Done from the page, which has `caches` too, rather than by messaging the
 * worker: a message to a worker that is not currently controlling the page is
 * silently dropped, and a sign-out is exactly when that is likely.
 */
export async function clearReferenceCache(): Promise<void> {
  if (typeof caches === 'undefined') {
    return;
  }
  try {
    await caches.delete(REFERENCE_CACHE);
  } catch {
    // Cache Storage refused (private mode in some browsers). There is nothing
    // stored to leak in that case either.
  }
}
